/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
import { get, isEmpty, map, cloneDeep, filter, keyBy } from 'lodash/fp';
import { set, isNumber, toNumber } from 'lodash';
import {
  getInStoreSelection,
  getDefaultRate,
  findMatchedRate,
} from '@upstart/harmony/lib/pen-utils';
import {
  TaxType,
  Origin,
  ProtectionPlansSelectionType,
} from '@upstart/harmony/lib/constants';
import { PayoffRequestStatus } from '@upstart/harmony/lib/legos/lossExpressTypes';
import { isValidProdigyVIN } from '@upstart/harmony/lib/car-utils';
import { findTradeInIndex } from '@upstart/harmony/lib/deal-utils';
import { fauxTradeId } from './constants';

// update properties for all offers of the given finance type
export const updateOffers = (pencil, financeType, offerProps) =>
  map((offer) => ({ ...offer, ...offerProps }))(pencil.data[financeType]);

// update properties for single offers of the given finance type
export const updateOffer = (pencil, financeType, index, offerProps) => [
  ...pencil.data[financeType].slice(0, index),
  {
    ...pencil.data[financeType][index],
    ...offerProps,
  },
  ...pencil.data[financeType].slice(index + 1),
];

export const duplicateOffer = (pencil, financeType, index) => [
  ...pencil.data[financeType].slice(0, index),
  cloneDeep(pencil.data[financeType][index]),
  ...pencil.data[financeType].slice(index),
];

export const removeOffer = (pencil, financeType, index) => [
  ...pencil.data[financeType].slice(0, index),
  ...pencil.data[financeType].slice(index + 1),
];

export const hasLeaseOffers = (pencil) => !isEmpty(get('data.lease')(pencil));

export const isAprFee = (fee) => isNumber(fee.apr);

export const getDefaultTaxTypeLabel = (taxType) => {
  switch (taxType) {
    case TaxType.COUNTY:
      return 'County Tax';
    case TaxType.CITY:
      return 'City Tax';
    case TaxType.INCEPTION:
      return 'Inception Tax';
    case TaxType.STATE:
    default:
      return 'State Tax';
  }
};

export const hasTaxAmountOverride = (taxBreakdown) =>
  Object.values(taxBreakdown || {}).find(
    (t) => t?.amountOverride !== undefined,
  );

const getPencilTradeInsWithLossExpress = (pencilTradeIns, dealTradeIns) => {
  if (!pencilTradeIns?.length || !dealTradeIns?.length) {
    return pencilTradeIns;
  }

  return pencilTradeIns.map((tradeIn) => {
    const idx = findTradeInIndex(tradeIn, dealTradeIns);
    if (
      idx > -1 &&
      dealTradeIns[idx].payoffRequestStatus === PayoffRequestStatus.Success
    ) {
      return { ...tradeIn, payOff: dealTradeIns[idx].payOff };
    }

    return tradeIn;
  });
};

function normalizeTradeIn(tradeIn) {
  if (!tradeIn) {
    return null;
  }

  if (tradeIn && !tradeIn.tradeInAllowance && tradeIn.tradeValue) {
    tradeIn.tradeInAllowance = tradeIn.tradeValue;
  }

  return {
    ...tradeIn,
    id: tradeIn.id ?? fauxTradeId,
    tradeACV: tradeIn.tradeACV,
    tradeInAllowance: tradeIn.tradeInAllowance,
    payOff: toNumber(tradeIn.payOff || tradeIn.tradePayoff || 0),
  };
}

// Compare TradeIns from both a Deal and a Pencil
// Extract only applicable TradeIns for display
// Exclude duplicates
// @param pencilTradeIns    Array of TradeIns stored in the Pencil
// @param dealTradeIns      Array of TradeIns stored in the Deal
export const getPencilTradeIns = (
  pencilTradeIns,
  dealTradeIns,
  tradeInAllowance,
  tradePayoff,
  tradeACV,
  tradeInTaxCredit,
) => {
  // if the deal has a payoff from loss express, use that
  pencilTradeIns = getPencilTradeInsWithLossExpress(
    pencilTradeIns,
    dealTradeIns,
  );

  const pencilTradeInsCount = (pencilTradeIns && pencilTradeIns.length) || 0;
  const dealTradeInsCount = (dealTradeIns && dealTradeIns.length) || 0;

  const assignValuesToFirstTrade = (tradeList) =>
    [...tradeList].map((trade, idx) => {
      if (idx === 0) {
        return { ...trade, tradeInAllowance, tradeACV, payOff: tradePayoff };
      }
      return { ...trade, tradeInAllowance: 0, tradeACV: 0, payOff: 0 };
    });

  const dealHasNonEmptyTrade =
    !!dealTradeIns &&
    dealTradeIns.some(
      (tradeIn) =>
        !!(
          tradeIn.payOff ||
          tradeIn.tradeACV ||
          tradeIn.tradeValue ||
          tradeIn.tradeInAllowance
        ),
    );

  /**
   * This is a special edge case which solves handles bug
   * from https://upstartnetwork.atlassian.net/browse/REL-11053
   *
   * When one trade in have been added to the deal and Pencil has only one faux-trade-in.
   * We need to populate these fields tradeInAllowance, payOff, tradeACV and tradeInTaxCredit.
   * We will populate the above values in this order desking offer's tradeIn values
   * followed by deal tradeIns values followed by pencil tradeIn values.
   *  */

  if (
    dealTradeIns?.length === 1 &&
    pencilTradeIns?.length === 1 &&
    pencilTradeIns[0]?.id === fauxTradeId
  ) {
    return [
      {
        ...dealTradeIns[0],
        id: dealTradeIns[0].id ?? fauxTradeId,
        tradeACV:
          tradeACV || dealTradeIns[0].tradeACV || pencilTradeIns[0].tradeACV,
        tradeInAllowance:
          tradeInAllowance ||
          dealTradeIns[0].tradeInAllowance ||
          pencilTradeIns[0].tradeInAllowance,
        payOff: toNumber(
          tradePayoff ||
            dealTradeIns[0].payOff ||
            dealTradeIns[0].tradePayoff ||
            pencilTradeIns[0].payOff ||
            pencilTradeIns[0].tradePayoff ||
            0,
        ),
        tradeInTaxCredit:
          tradeInTaxCredit ||
          dealTradeIns[0].tradeInTaxCredit ||
          pencilTradeIns[0].tradeInTaxCredit,
      },
    ];
  }

  // When trade ins have been added to the deal, but we haven't done anything with them in chairman yet
  if (
    dealTradeInsCount &&
    (!pencilTradeInsCount || pencilTradeIns[0].id === fauxTradeId)
  ) {
    // If we've started entering some values on a deal trade in, assume those values are
    // more accurate and preferred over any preliminary values set on the pencil before
    // a trade in was added.
    if (dealHasNonEmptyTrade) {
      return dealTradeIns.map(normalizeTradeIn);
    }

    // Otherwise, if all the trades on the deal are empty lets map any pencil values
    // into the first trade
    return assignValuesToFirstTrade(dealTradeIns.map(normalizeTradeIn));
  }

  if (pencilTradeInsCount > 0 && dealTradeIns) {
    // Because tradeIns in the Deal can be updated by the user
    // let's compare our PencilTradeIns to the Deal TradeIns
    // Only return tradeIns that are present in the Deal
    // If they are not in the Deal, we will consider them out of date
    const updatedPencilTradeIns = [];
    const dealTradeInsCopy = cloneDeep(dealTradeIns) || [];

    pencilTradeIns.forEach((pt) => {
      const idx = findTradeInIndex(pt, dealTradeInsCopy);
      if (idx > -1) {
        // If the PencilTradeIn is found in the DealTradeins
        // we can add it to our new array

        const { tradeInAllowance: dealTradeInAllownace, payOff } =
          dealTradeInsCopy[idx];
        const pencilTrade = {
          ...pt,
          tradeInAllowance: dealTradeInAllownace,
          payOff,
        };
        updatedPencilTradeIns.push(pencilTrade);
        // we also want to set this entry in DealTradeIns
        // to null so when we merge below, we won't have duplicates
        dealTradeInsCopy[idx] = null;
      }
    });

    // For any remaining unmapped dealTradeIns (newly added tradeIns for example),
    // we must ensure that tradeValue is mapped to tradeAllowance
    const dealTradeInsMappedToPencil = dealTradeInsCopy
      .map(normalizeTradeIn)
      .filter((x) => x !== null);

    // Return PencilTradeIns
    return [].concat(updatedPencilTradeIns, dealTradeInsMappedToPencil);
  }

  // Neither the Deal nor the Pencil have tradeIns
  // return a single empty tradeIn entry
  // 'faux-trade-in' signifies no tradein is present in the deal
  // In this case, sales person may still want
  // to add a trade allowance
  return [
    {
      id: fauxTradeId,
      tradeACV,
      tradeInAllowance,
      payOff: tradePayoff,
    },
  ];
};

export const getPencilWithTradeIns = (pencil, deal) => {
  let pencilTradeIns = getPencilTradeIns(
    pencil?.data?.pencilTradeIns,
    deal?.tradeIns,
    pencil?.data?.tradeInAllowance ?? 0,
    pencil?.data?.tradePayoff ?? 0,
    pencil?.data?.tradeACV ?? 0,
  );
  if (pencilTradeIns?.length > 0) {
    pencilTradeIns = pencilTradeIns.map(normalizeTradeIn);

    const totalTradeInsPayoff = pencilTradeIns.reduce(
      (total, trade) => total + (trade.payOff || 0),
      0,
    );
    const totalTradeInsACV = pencilTradeIns.reduce(
      (total, trade) => total + (trade.tradeACV || 0),
      0,
    );
    const totalTradeInsAllowance = pencilTradeIns.reduce(
      (total, trade) => total + (trade.tradeInAllowance || 0),
      0,
    );
    set(pencil, ['data', 'pencilTradeIns'], pencilTradeIns);
    set(pencil, ['data', 'tradeACV'], totalTradeInsACV);
    set(pencil, ['data', 'tradePayoff'], totalTradeInsPayoff);
    set(pencil, ['data', 'tradeInAllowance'], totalTradeInsAllowance);
  }
  return pencil;
};

export const getProductsRatesAndTerms = (
  car,
  deal,
  dealer,
  financeType,
  pencil,
  rates,
  preSelectedBundles,
  preSelectedProducts,
) => {
  const options = {};
  let bundleMap = {};
  let productMap = {};
  if (
    !car ||
    !dealer ||
    (!pencil && !preSelectedBundles && !preSelectedProducts) ||
    !rates
  ) {
    return { options, bundleMap, productMap };
  }

  const { penConfig, penProducts } = dealer;

  // get bundles by car condition and financeType
  const bundleList = get(`bundles.${car.condition}.${financeType}]`)(penConfig);
  const availableBundles = filter({ enabled: true })(cloneDeep(bundleList));
  bundleMap = keyBy((b) => b.id)(availableBundles);

  // get products by car condition and financeType
  const availableProducts = filter({
    availability: { [car.condition]: { [financeType]: true } },
    enabled: true,
  })(penProducts);
  productMap = keyBy((p) => p.id)(cloneDeep(availableProducts));

  if (isEmpty(availableBundles) && isEmpty(availableProducts)) {
    return { options, bundleMap, productMap };
  }

  let selectedBundles = [];
  let selectedProducts = [];

  if (preSelectedBundles && preSelectedProducts) {
    selectedBundles = preSelectedBundles;
    selectedProducts = preSelectedProducts;
  } else if (
    get(`data.selectedBundles.${financeType}`)(pencil) &&
    get(`data.selectedProducts.${financeType}`)(pencil)
  ) {
    selectedBundles = pencil.data.selectedBundles[financeType] || [];
    selectedProducts = pencil.data.selectedProducts[financeType] || [];
  } else if (deal && !deal.hasActiveChat) {
    // Preselect plans from the deal for the primary vehicle if pencil is not available
    if (
      deal.selectedBundleIds ||
      (deal.selectedProducts && car.id === deal.car_id)
    ) {
      selectedBundles = deal?.selectedBundleIds?.map((id) => ({ id }));
      selectedProducts = deal?.selectedProducts;
    }
    // Preselect mandatory products from mandatory
    Object.keys(bundleMap).forEach((bundleId) => {
      bundleMap[bundleId].productIds.forEach((id) => {
        const inStoreSelection = getInStoreSelection(bundleMap[bundleId]);
        const shouldBeSelected =
          inStoreSelection === ProtectionPlansSelectionType.Mandatory ||
          inStoreSelection === ProtectionPlansSelectionType.Default;
        if (
          productMap[id] &&
          shouldBeSelected &&
          !isValidProdigyVIN(car?.id) &&
          (deal.origin === Origin.INSTORE || deal.origin === Origin.DESKING)
        ) {
          bundleMap[bundleId].selected = true;
          productMap[id].selected = true;
          productMap[id].selectedInStore = inStoreSelection;
        }
      });
    });
    availableProducts.forEach((product) => {
      const inStoreSelection = getInStoreSelection(product);
      if (
        productMap[product.id] &&
        (inStoreSelection === ProtectionPlansSelectionType.Mandatory ||
          inStoreSelection === ProtectionPlansSelectionType.Default) &&
        product.enabled &&
        !isValidProdigyVIN(car?.id) &&
        (deal.origin === Origin.INSTORE || deal.origin === Origin.DESKING)
      ) {
        productMap[product.id].selected = true;
        productMap[product.id].selectedInStore = inStoreSelection;
      }
    });
  }

  // pre-select bundles from selectedBundleIds
  (selectedBundles || []).forEach((bundle) => {
    const bundleId = bundle.id;
    if (bundleMap[bundleId]) {
      bundleMap[bundleId].selected = true;
      bundleMap[bundleId].productIds.forEach((id) => {
        if (productMap[id]) {
          productMap[id].selected = true;
        }
      });
    }
  });

  // pre-select products from selectedProducts
  if (!isEmpty(selectedProducts)) {
    selectedProducts.forEach((p) => {
      if (productMap[p.productId]) {
        productMap[p.productId].selected = true;
        productMap[p.productId].inCapCostForLease = p.inCapCostForLease;
        productMap[p.productId].inCapCostForOnePayLease =
          p.inCapCostForOnePayLease;
      }
    });
  }

  // pick a default term from rates
  Object.keys(rates).forEach((id) => {
    if (productMap[id]) {
      const defaultRate = getDefaultRate(rates[id]);
      productMap[id].term = get('term')(defaultRate);
      productMap[id].miles = get('miles')(defaultRate);
      productMap[id].bundlePrices = get('bundlePrices')(defaultRate);
      productMap[id].retailPrice = get('deductible.retailPrice')(defaultRate);
      productMap[id].monthlyPayment = get('deductible.monthlyPayment')(
        defaultRate,
      );
      productMap[id].minRetailPrice = get('deductible.minRetailPrice')(
        defaultRate,
      );
      productMap[id].maxRetailPrice = get('deductible.maxRetailPrice')(
        defaultRate,
      );
      productMap[id].coverageName = get('coverageName')(defaultRate);

      const selectedProduct = selectedProducts.find(
        (p) => p && p.productId === id,
      );
      if (selectedProduct) {
        productMap[id].term = selectedProduct.termMonths;
        productMap[id].miles = selectedProduct.termMiles;
        productMap[id].maxRetailPrice = selectedProduct.maxRetailPrice;
        productMap[id].minRetailPrice = selectedProduct.minRetailPrice;
        productMap[id].coverageName = selectedProduct.coverageName;

        const { bundlePrices, deductible } =
          findMatchedRate({
            rates: rates[id],
            termMiles: selectedProduct.termMiles,
            termMonths: selectedProduct.termMonths,
            coverageName: selectedProduct.coverageName,
          }) || {};
        if (bundlePrices) {
          productMap[id].bundlePrices = bundlePrices;
        }
        if (deductible) {
          productMap[id].retailPrice = deductible.retailPrice;
          productMap[id].monthlyPayment = deductible.monthlyPayment;
        }
      }
    }
    // prep term options for select dropdown
    options[id] = get(id)(rates).map((t) => ({
      term: t.term || 0,
      miles: t.miles || 0,
      deductible: t.deductible,
      bundlePrices: t.bundlePrices,
      renderValue: `${t.term || 0} mo ${t.miles || 0} miles`,
      coverageName: t.coverageName,
      coverageDisplayName: t.coverageDisplayName,
    }));
  });
  return { bundleMap, productMap, options };
};

export const getFinanceApplicationForResponse = (pencil, financeResponse) =>
  pencil?.data?.financeApplications?.find(
    (a) => a.id === financeResponse?.applicationId,
  );
